import { storeTimeZone } from "../utils/dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { action, API, controller } from "api";
import { ToFormData } from "hooks";
import { useAppContext } from "context";
import dayjs from "../utils/dayjs";

const readCount = (res) =>
  res?.totalRecord ?? (Array.isArray(res?.result) ? res.result.length : 0) ?? 0;

/**
 * Shared "reminders" data — cheap count calls (rowsPerPage:1, reading
 * totalRecord) for the alerts surfaced by both the dashboard Reminders portlet
 * and the shell Notifications bell. Each item is access-gated.
 *
 * Returns { items: [{ key, label, tone, path, count }], total, loading }.
 */
const useReminderCounts = () => {
  const { t } = useTranslation(["common"]);
  const { access, selCompany } = useAppContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selCompany) return;
    setLoading(true);
    const today = dayjs().format("YYYY-MM-DD");

    const stockPayload = (statusFilter) =>
      ToFormData({
        companyId: selCompany,
        asOfDate: today,
        statusFilter,
        showZeroStock: true,
        pageNum: 0,
        rowsPerPage: 1,
        takeAll: false,
        timeZone: storeTimeZone() || "",
      });

    const pg = ToFormData({ companyId: selCompany, pageNum: 0, rowsPerPage: 1 });

    const defs = [
      {
        key: "outStock",
        enabled: !!access.report?.stockReport,
        label: t("outOfStock", "Out of stock"),
        tone: "red",
        path: "stock_report",
        run: () => API(controller.StockReport + action.StockReport).post(stockPayload("out")),
      },
      {
        key: "lowStock",
        enabled: !!access.report?.stockReport,
        label: t("lowStock", "Low stock"),
        tone: "amber",
        path: "stock_report",
        run: () => API(controller.StockReport + action.StockReport).post(stockPayload("low")),
      },
      {
        key: "creditCustomers",
        enabled: !!access.report?.creditCustomer,
        label: t("customersOverCredit", "Customers over credit limit"),
        tone: "red",
        path: "credit_customer_report",
        run: () => API(controller.Report + action.CreditCustomer).post(pg),
      },
      {
        key: "countsDue",
        enabled: !!access.inventoryCountAccess,
        label: t("inventoryCountsDue", "Inventory counts due"),
        tone: "amber",
        path: "inventory_counts",
        run: () => API(controller.InventoryCount + action.DueCounts).post(pg),
      },
      {
        key: "inquiries",
        enabled: !!access.inqueryAccess,
        label: t("openInquiries", "Inquiries"),
        tone: "info",
        path: "inquiries",
        run: () => API(controller.Inquiry + action.GetAllByCo).post(pg),
      },
    ].filter((d) => d.enabled);

    let cancelled = false;
    Promise.all(
      defs.map((d) =>
        d.run().then(
          (res) => ({ key: d.key, label: d.label, tone: d.tone, path: d.path, count: readCount(res) }),
          () => ({ key: d.key, label: d.label, tone: d.tone, path: d.path, count: 0 }),
        ),
      ),
    ).then((results) => {
      if (cancelled) return;
      setItems(results);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCompany, access]);

  const total = items.reduce((sum, i) => sum + (i.count || 0), 0);
  return { items, total, loading };
};

export default useReminderCounts;
